import { useTranslation } from 'react-i18next'
import { Text, Stack, Box } from '@machobear/design-system'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'

import { ReactNode, useState } from 'react'
import { colors, breakpoints, structure, fontWeights } from '@/styles'
import useMediaQuery from '@/hooks/use-media-query'
import { FilterBar, FilterTextWrapper } from '@/components/search-filter'
import { TabMenu } from '@/components/tabs'

import AreaTitle from '@/components/area-title'
import SearchBar from '@/components/search-bar'

const PageHeader = styled.div<{
  $height: number
  padding?: string
  top?: number
}>`
  top: ${props => props.top ?? 0}px;
  position: sticky;
  width: 100%;
  z-index: 20;
  background-color: ${colors.white};
  height: ${props => props.$height}px;
  padding: ${props => props.padding ?? '0'};

  @media only screen and (max-width: ${breakpoints.tabletLandscape}) {
    height: 100px;
  }

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    box-shadow: 0 2px 8px 0 rgba(146, 146, 146, 0.69);
    height: 158px;
  }

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    top: ${structure.header.heightMobile}px;
    height: 158px;
  }
`

const PageHeaderOutter = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
`

const PageHeaderInner = styled.div<{ padding?: string; maxWidth?: string }>`
  width: calc(100% - 100px);
  margin: 0 40px;
  padding: ${props => props.padding ?? '45px 0 30px'};
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  background: ${colors.white};

  @media only screen and (max-width: ${breakpoints.desktop}) {
    margin: 0 40px;
    width: calc(100% - 60px);
  }

  @media only screen and (max-width: ${breakpoints.desktopSmall}) {
    margin: 0 40px;
  }

  @media only screen and (max-width: ${breakpoints.tabletLandscape}) {
    margin: 0 40px;
    padding: 20px 0;
    width: unset;
  }

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin: 0 30px;
  }

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    justify-content: center;
    margin: 0 10px;
  }
`

const HeaderTitle = styled.h1`
  font-size: 30px;
  color: ${colors.regular};
  font-weight: ${fontWeights.bold};
  line-height: 41px;
  margin: 0;
  text-overflow: ellipsis;
  overflow: hidden;

  @media only screen and (max-width: ${breakpoints.tabletLandscape}) {
    display: none;
  }
`

const FiltersWrapper = styled.div`
  display: flex;
  justify-content: space-between;

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    flex-direction: column;
    align-items: center;
    gap: 12px 0;
  }
  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    gap: 0;
  }
`

const TrustScoreTableHeader = ({
  height = 158,
  title,
  description = '',
  onSearch,
  children
}: {
  height?: number
  title: string | null
  description?: string
  onSearch: (value: string) => void
  children: ReactNode
}) => {
  const { t } = useTranslation()
  const location = useLocation()
  const pathName = location.pathname
  const isSettings = pathName.includes('manage')
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const isTabletLandscape = useMediaQuery(`(max-width: ${breakpoints.tabletLandscape})`)
  const headerHeight = isSettings ? 200 : height
  const [searchString, setSearchString] = useState('')
  const onSearchWithScroll = async (value: any) => {
    const output = await onSearch(value)
    return output
  }

  return (
    <PageHeader
      $height={headerHeight}
      top={isSettings ? 0 : 64}
      padding={isSettings ? '0 0 60px' : undefined}
    >
      <PageHeaderOutter>
        <PageHeaderInner padding={isSettings ? '0' : undefined}>
          {isSettings ? (
            <Stack space="xxsmall">
              {!isMobile && (
                <Box>
                  <AreaTitle textAlign="left" top={40} bottom={10}>
                    {title}
                  </AreaTitle>

                  <Text tone="secondary" size="small">
                    {description}
                  </Text>
                </Box>
              )}

              <FiltersWrapper>
                {children}
                <Box style={{ width: isMobile ? '100%' : isTabletLandscape ? '310px' : '400px' }}>
                  <SearchBar
                    placeholder={t('common.search', 'Search')}
                    onSearch={onSearchWithScroll}
                    fullWidth
                    searchString={searchString}
                    setSearchString={setSearchString}
                  />
                </Box>
              </FiltersWrapper>
            </Stack>
          ) : (
            <>
              <HeaderTitle>{title}</HeaderTitle>
              <FiltersWrapper>
                {children}

                <Box style={{ width: isMobile ? '100%' : isTabletLandscape ? '310px' : '400px' }}>
                  {!isMobile ? (
                    <Box height="touchable" display="flex" gap="gutter" width="full">
                      <SearchBar
                        placeholder={t('common.search', 'Search')}
                        onSearch={onSearchWithScroll}
                        fullWidth
                        searchString={searchString}
                        setSearchString={setSearchString}
                      />
                    </Box>
                  ) : (
                    <Box display="flex" paddingX="small" width="full">
                      <SearchBar
                        placeholder={t('common.search', 'Search')}
                        onSearch={onSearchWithScroll}
                        fullWidth
                        searchString={searchString}
                        setSearchString={setSearchString}
                      />
                    </Box>
                  )}
                </Box>
              </FiltersWrapper>
            </>
          )}
        </PageHeaderInner>
      </PageHeaderOutter>
    </PageHeader>
  )
}

const TrustScoreHeader = ({
  tabCount,
  currentTab,
  setPage,
  setCurrentTab,
  onSearch
}: {
  tabCount: {
    all: string
    completed: string
    pending: string
    draft: string
    failed: string
  }
  currentTab: string
  setPage: (val: number) => void
  setCurrentTab: (val: string) => void
  onSearch: (searchString: string) => void
  refetch: () => void
}) => {
  const { t } = useTranslation()
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)

  const menuItems = [
    {
      key: 'all',
      onClick: () => {
        setPage(1)
        setCurrentTab('all')
      },
      isActive: currentTab === 'all',
      children: (
        <FilterTextWrapper>
          {t('l.standalone_trustscore.list.tab.all', 'All')} <span>({tabCount?.all})</span>
        </FilterTextWrapper>
      )
    },
    {
      key: 'complete',
      onClick: () => {
        setPage(1)
        setCurrentTab('completed')
      },
      isActive: currentTab === 'completed',
      children: (
        <FilterTextWrapper>
          {t('l.standalone_trustscore.list.tab.completed', 'Completed')}{' '}
          <span>({tabCount?.completed})</span>
        </FilterTextWrapper>
      )
    },
    {
      key: 'failed',
      onClick: () => {
        setPage(1)
        setCurrentTab('failed')
      },
      isActive: currentTab === 'failed',
      children: (
        <FilterTextWrapper>
          {t('l.standalone_trustscore.list.tab.failed', 'Failed')} <span>({tabCount?.failed})</span>
        </FilterTextWrapper>
      )
    },
    {
      key: 'draft',
      onClick: () => {
        setPage(1)
        setCurrentTab('draft')
      },
      isActive: currentTab === 'draft',
      children: (
        <FilterTextWrapper>
          {t('l.standalone_trustscore.list.tab.draft', 'Draft')} <span>({tabCount?.draft})</span>
        </FilterTextWrapper>
      )
    }
  ]

  return (
    <TrustScoreTableHeader title={null} onSearch={onSearch} height={125}>
      <FilterBar>
        <TabMenu
          options={menuItems}
          theme={displayMobile ? 'primary' : 'secondary'}
          size="medium"
          height={displayMobile ? 44 : undefined}
        />
      </FilterBar>
    </TrustScoreTableHeader>
  )
}

export default TrustScoreHeader
